import { useQuery } from '@apollo/client';
import { OndemandVideo } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { Box } from '@mui/system';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useDeleteChannel } from '~/api/channels';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DetailTextField } from '~/components/forms/details';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { NetworkChannelAnalysisDocument } from '../queries/analysis.generated';
import { useTabs } from './lib/tabs';

export const ChannelAnalysis = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const navigate = useNavigate();

  const link = useLink();

  const params = useParams<{ channelId: string }>();

  const channelId = Number(params.channelId);

  const tabs = useTabs();

  const { data, loading, error } = useQuery(NetworkChannelAnalysisDocument, {
    variables: { networkId: currentNetwork.id, channelId },
  });

  const [deleteChannel] = useDeleteChannel();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const channel = data?.network?.channel;

  const handleDelete = useCallback(async () => {
    if (!(await confirmDelete())) return;
    await deleteChannel({
      variables: { input: { channelIds: [channelId] } },
    });
    navigate(link('/channels'), { replace: true });
  }, [confirmDelete, deleteChannel, channelId, link, navigate]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <Toolbar
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title={channel?.canDestroy.reasons?.fullMessages.join(', ')}>
              <span>
                <DeleteButton disabled={!channel?.canDestroy.value} onClick={handleDelete} />
              </span>
            </Tooltip>
          </Box>
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<OndemandVideo />}
        titleText={channel?.name}
      />
      <ScrollableRouterTabs current={t('pages.channels.detail.tabs.analysis')} tabs={tabs} />
      <PageContainer>
        {channel?.metadata?.audioStream && channel.metadata.videoStream ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <SettingsHead>
                {t('pages.channels.detail.analysis.video_stream_heading')}
              </SettingsHead>
              <SettingsBody>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.codec_full_name_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.codec_full_name_video_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="codecLongName"
                      value={channel.metadata.videoStream.codecLongName ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.channels.detail.analysis.codec_label')}</SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.codec_video_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="codecName"
                      value={channel.metadata.videoStream.codecName ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.frame_rate_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.frame_rate_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="avgFrameRate"
                      value={channel.metadata.videoStream.frameRate ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.channels.detail.analysis.scan_type_label')}</SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.scan_type_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="fieldOrder"
                      value={channel.metadata.videoStream.fieldOrder ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.aspect_ratio_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.aspect_ratio_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="displayAspectRatio"
                      value={channel.metadata.videoStream.displayAspectRatio ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.resolution_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.resolution_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="resolution"
                      value={channel.metadata.videoStream.resolution}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.pixel_format_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.pixel_format_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="pixFmt"
                      value={channel.metadata.videoStream.pixFmt ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.color_space_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.color_space_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="colorSpace"
                      value={channel.metadata.videoStream.colorSpace ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </Box>
            <Box>
              <SettingsHead>
                {t('pages.channels.detail.analysis.audio_stream_heading')}
              </SettingsHead>
              <SettingsBody>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.codec_full_name_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.codec_full_name_audio_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="codecLongName"
                      value={channel.metadata.audioStream.codecLongName ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.channels.detail.analysis.codec_label')}</SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.codec_audio_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="codecName"
                      value={channel.metadata.audioStream.codecName ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.channels.detail.analysis.bitrate_label')}</SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.bitrate_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="bitRate"
                      value={channel.metadata.audioStream.bitRate ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.sample_rate_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.sample_rate_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="sampleRate"
                      value={channel.metadata.audioStream.sampleRate ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData>
                  <SettingLabelWrapper>
                    <SettingName>
                      {t('pages.channels.detail.analysis.channel_layout_label')}
                    </SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.channel_layout_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="channelLayout"
                      value={channel.metadata.audioStream.channelLayout ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>{t('pages.channels.detail.analysis.channels_label')}</SettingName>
                    <SettingDescription>
                      {t('pages.channels.detail.analysis.channels_description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <DetailTextField
                      disabled
                      fullWidth
                      name="channels"
                      value={channel.metadata.audioStream.channels ?? ''}
                    />
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </Box>
          </Box>
        ) : (
          <Alert severity="info">{t('pages.channels.detail.analysis.no_data')}</Alert>
        )}
      </PageContainer>
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={
          <span>
            {t('pages.channels.detail.delete.prompt')}{' '}
            <b>{t('pages.channels.detail.delete.warning')}</b>
          </span>
        }
        title={t('pages.channels.detail.delete.title')}
      />
    </LoadingPane>
  );
};
